/* eslint-disable max-lines-per-function */
import type {FormEventHandler, RefObject} from 'react';
import {useContext, useEffect, useRef} from 'react';

import {action} from 'mobx';
import {useLocalObservable} from 'mobx-react-lite';

import {FormsDataContext} from 'UI/components/action/GlobalFormProvider';

import type {FormField, FormState, FormValue, HandleFormSubmit, ValidationFunc} from 'Shared/formHelperTypes';

/**
 * Internal Form Hook.
 *
 * This hook provides form functionality such as managing form state, handling form changes,
 * resetting the form, and submitting the form. It is designed to be used within the `FormComp`
 * component.
 *
 * @param id       The unique identifier for the form.
 * @param onChange The callback function triggered when a form field value changes.
 * @param onError  The callback function triggered when the form has an error.
 * @param onReset  The callback function triggered when the form is reset.
 * @param onSubmit The callback function triggered when the form is submitted.
 *
 * @returns An object containing the form reference, form state, and form handling functions.
 */
export const useInternalForm = (
    id: string,
    onChange: ((name: string, value: FormValue) => void) | undefined,
    onError: () => void,
    onReset: FormEventHandler<HTMLFormElement>,
    onSubmit: HandleFormSubmit
) => {
    const globalFormData = useContext(FormsDataContext);
    const form = useRef<HTMLFormElement>(null);
    const formFieldState = useLocalObservable<FormState>(() => ({
        errors: {},
        fields: {},
        /**
         * Whether the form has errors.
         *
         * @returns A boolean indicating whether the form has errors.
         */
        get hasErrors() {
            return Object.values(this.errors).some(error => error !== null);
        },
        initialValues: {},
        validations: {},
        values: {},
        /**
         * Add a form field to the form.
         *
         * @param name         The name of the form field.
         * @param defaultValue The default value of the form field.
         * @param initialValue The initial value of the form field.
         * @param validation   The validation function for the form field.
         */
        addField(
            name: string,
            defaultValue: FormValue,
            initialValue: FormValue,
            validation: ValidationFunc | undefined
        ) {
            action(() => {
                if (!(name in this.values) && typeof this.values[String(name)] === 'undefined') {
                    this.values[String(name)] = defaultValue;
                }
                if (!(name in this.initialValues) && typeof this.initialValues[String(name)] === 'undefined') {
                    this.initialValues[String(name)] = initialValue;
                }
                if (!(name in this.errors) && typeof this.errors[String(name)] === 'undefined') {
                    this.errors[String(name)] = null;
                }
                if (!(name in this.validations) && typeof this.validations[String(name)] === 'undefined') {
                    this.validations[String(name)] = validation;
                }
            })();
        },
        /**
         * Registers a field reference in the form fields collection using the provided field name and reference object.
         * This method allows dynamically adding field references to the form state, enabling access to field properties and methods outside of the normal render flow. The field reference is stored in an internal collection, keyed by the field name, facilitating easy retrieval and manipulation of field instances. This mechanism is crucial for managing complex forms where fields may need to be accessed or updated programmatically based on user interactions or other conditions.
         *
         * @param name     The name of the field to register, acting as a unique identifier within the form.
         * @param fieldRef The reference object pointing to the field instance, used to access the field's current state and methods.
         */
        addFieldRef(name: string, fieldRef: RefObject<FormField | null>) {
            this.fields[String(name)] = fieldRef;
        },
        /**
         * Changes the validation function for a specific form field.
         *
         * @param name       The name of the form field.
         * @param validation The new validation function for the form field.
         */
        changeValidation(name: string, validation: ValidationFunc | undefined) {
            this.validations[String(name)] = validation;
        },
        /**
         * Change form field values.
         *
         * @param name  The name of the form field.
         * @param value The new value of the form field.
         */
        changeValue(name: string, value: FormValue) {
            this.validateField(name, value);
            this.values[String(name)] = value;
            onChange?.(name, value);
        },
        /**
         * Deletes values from the form.
         *
         * @param name The name of the form field.
         */
        deleteValue(name: string) {
            delete this.values[String(name)];
            delete this.initialValues[String(name)];
            delete this.errors[String(name)];
            delete this.validations[String(name)];
        },
        /**
         * Resets an specific form field value.
         *
         * @param name The name of the form field.
         */
        resetValue(name: string) {
            if (name in this.values && typeof this.values[String(name)] !== 'undefined') {
                this.values[String(name)] = this.initialValues[String(name)];
                this.errors[String(name)] = null;
            }
        },
        /**
         * Change form field values.
         */
        resetValues() {
            Object.entries(this.initialValues).forEach(([key, objValue]) => {
                this.values[String(key)] = objValue;
            });
            Object.entries(this.errors).forEach(([key]) => {
                this.errors[String(key)] = null;
            });
        },
        /**
         * Validates a specific form field by its name, checking the current value against the associated validation function.
         * If the validation function returns an error message, it updates the form's error state for that field and sets the custom validity on the field reference to indicate an invalid state. If the validation passes without errors, it clears any existing errors for that field and resets the custom validity to indicate a valid state. This method ensures that individual form fields are validated in real-time as their values change, providing immediate feedback to users and maintaining the integrity of the form data.
         *
         * @param name  The name of the form field to validate.
         * @param value The value to validate, which can be optionally provided to validate against a specific value instead of the current form state.
         */
        validateField(name: string, value?: FormValue) {
            const currentValue = value ?? this.values[String(name)];
            const validation = this.validations[String(name)]?.(currentValue, this.values);

            if (validation) {
                this.errors[String(name)] = validation;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                this.fields[String(name)]?.current?.setCustomValidity('Invalid field');
            } else {
                this.errors[String(name)] = null;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                this.fields[String(name)]?.current?.setCustomValidity('');
            }
        },
        /**
         * Validate the form fields.
         */
        validateFields() {
            Object.entries(this.values).forEach(([key, value]) => {
                const validation = this.validations[String(key)]?.(value, this.values);

                if (validation) {
                    this.errors[String(key)] = validation;
                } else {
                    this.errors[String(key)] = null;
                }
            });
        }
    }));

    /**
     * Handle form reset.
     *
     * This function is called when the form is reset. It restores the form field values
     * to their initial values and calls the `onReset` callback.
     *
     * @param e The form event object.
     */
    const handleReset: FormEventHandler<HTMLFormElement> = e => {
        e.preventDefault();
        e.stopPropagation();

        formFieldState.resetValues();

        if (form.current) {
            form.current.reset();
        }

        onReset(e);
    };

    /**
     * Handle form submission.
     *
     * This function is called when the form is submitted. It prevents the default form submission,
     * stops the event propagation, and calls the `onSubmit` callback with the form values and a
     * `getForm` method to retrieve the form data object.
     *
     * @param e The form event object.
     */
    const handleSubmit: FormEventHandler<HTMLFormElement> = async e => {
        e.preventDefault();
        e.stopPropagation();

        formFieldState.validateFields();

        if (formFieldState.hasErrors) {
            await onError();
            return;
        }

        await onSubmit({
            ctx: formFieldState,
            values: formFieldState.values,
            /**
             * Gets the form data object.
             *
             * @returns The form data object.
             */
            getForm() {
                return new FormData(form.current!);
            }
        });
    };

    const formState = {
        form: formFieldState,
        formId: id
    };

    useEffect(() => {
        if (globalFormData) {
            globalFormData.setFormData(id, formFieldState);
        }

        return () => {
            if (globalFormData) {
                globalFormData.deleteFormData(id);
            }
        };
    }, [formFieldState, globalFormData, id]);

    return {
        form,
        formState,
        handleReset,
        handleSubmit
    };
};